import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../hooks/useApi';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import DateInput from '../components/DateInput';
import { SectionProduitFini } from '../components/TracabiliteSections';
import EtapeTracabilite from '../components/EtapeTracabilite';
import BatchAnalytiquePanel from '../components/BatchAnalytiquePanel';
import CoutRevient from '../components/CoutRevient';
import BlocPrixVente from '../components/BlocPrixVente';
import CourbeCuissonBatch from '../components/CourbeCuissonBatch';
import RecetteStockPanel from '../components/RecetteStockPanel';
import { creerFormulaireTraiteFinal } from '../utils/tracabilite';
import Icon from '../components/Icon';
import './ProductionRibs.css';

// ─── CONSTANTES ──────────────────────────────

const ETAPES = [
  { id: 1, label: 'Batch' },
  { id: 2, label: 'Traçabilité' },
  { id: 3, label: 'Saisie' },
  { id: 4, label: 'Groupes' },
  { id: 5, label: 'Finaliser' },
];

const GROUPES_DEFAUT = [
  { nom: 'Small',  min: 1200, max: 1400 },
  { nom: 'Medium', min: 1401, max: 1650 },
  { nom: 'Large',  min: 1651, max: 1900 },
];

const GROUPE_COULEURS = {
  Small:       'var(--info)',
  Medium:      'var(--accent)',
  Large:       'var(--success)',
  'Hors plage': 'var(--danger)',
};

// ─── SOUS-COMPOSANTS ─────────────────────────

function Stepper({ etape, nbTracabiliteNonLies = 0, onStepClick, batch, isFinalise }) {
  const peutCliquer = (cible) => {
    if (!batch && cible > 1) return false;
    if (cible < etape) return true;           // retour arrière toujours libre
    if (isFinalise) return true;              // batch finalisé : tout accessible
    if (cible <= etape) return true;          // étape courante ou déjà faite
    // sauts vers l'avant : seulement si toutes les étapes précédentes sont complétées
    return etape >= cible - 1;
  };

  return (
    <div className="stepper">
      {ETAPES.map((e, i) => {
        const clickable = onStepClick && peutCliquer(e.id);
        return (
          <React.Fragment key={e.id}>
            <div
              className={`step ${etape === e.id ? 'active' : etape > e.id ? 'done' : ''} ${clickable ? 'step-cliquable' : ''}`}
              style={{ position: 'relative', cursor: clickable ? 'pointer' : 'default' }}
              onClick={() => clickable && onStepClick(e.id)}
              title={clickable ? `Aller à l'étape ${e.id} — ${e.label}` : ''}
            >
              <div className="step-circle">{etape > e.id ? <Icon name="check" size={14} weight="bold" /> : e.id}</div>
              {e.id === 2 && nbTracabiliteNonLies > 0 && (
                <span className="step-badge-warning">{nbTracabiliteNonLies}</span>
              )}
              <span className="step-label">{e.label}</span>
            </div>
            {i < ETAPES.length - 1 && (
              <div className={`step-line ${etape > e.id ? 'done' : ''}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function StatBadge({ label, value, color }) {
  return (
    <div className="stat-badge" style={{ borderColor: color }}>
      <span className="stat-value" style={{ color }}>{value}</span>
      <span className="stat-label">{label}</span>
    </div>
  );
}

// ─── ÉTAPE 1 : BATCH ─────────────────────────

function EtapeBatch({ onBatchCree }) {
  const [batches, setBatches] = useState([]);
  const [mode, setMode] = useState('liste');
  const [form, setForm] = useState({
    nom: '',
    date_prod: new Date().toISOString().slice(0, 10),
    operateurs: '',
    fournisseur: '',
    prix_kg: '',
    poids_cru_kg: '',
    mode_prix_vente: 'kg',
    prix_vente_kg: '',
    prix_vente_unite: '',
    notes: '',
  });
  const [nomAuto, setNomAuto] = useState('');
  const [nomManuel, setNomManuel] = useState(false);
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur] = useState('');
  const [batchASupprimer, setBatchASupprimer] = useState(null);
  const [suppLoading, setSuppLoading] = useState(false);
  const [toast, setToast] = useState('');
  const [batchAnalytique, setBatchAnalytique] = useState(null);
  const [pdfLoadingId, setPdfLoadingId] = useState(null);

  const telechargerPdf = async (batch) => {
    setPdfLoadingId(batch.id);
    try {
      const blob = await api.blob(`/ribs/batches/${batch.id}/pdf`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ribs-${batch.nom.replace(/\s+/g, '-')}-paires.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      afficherToast(`PDF ${batch.nom} téléchargé ✓`);
    } catch {
      afficherToast(`Erreur téléchargement PDF`);
    } finally {
      setPdfLoadingId(null);
    }
  };

  useEffect(() => {
    api.get('/ribs/batches').then(setBatches).catch(() => {});
  }, []);

  useEffect(() => {
    if (mode === 'nouveau' && !nomManuel) {
      api.get('/batches/prochain-numero/cotes_levees')
        .then(r => { setNomAuto(r.nom); setForm(f => ({ ...f, nom: r.nom })); })
        .catch(() => {});
    }
  }, [mode]); // eslint-disable-line

  const afficherToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3500);
  };

  const supprimerBatch = async () => {
    if (!batchASupprimer) return;
    setSuppLoading(true);
    try {
      await api.delete(`/ribs/batches/${batchASupprimer.id}`);
      setBatches(prev => prev.filter(b => b.id !== batchASupprimer.id));
      afficherToast(`Batch ${batchASupprimer.nom} supprimé ✓`);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur lors de la suppression');
    } finally {
      setSuppLoading(false);
      setBatchASupprimer(null);
    }
  };

  const creerBatch = async () => {
    if (!form.date_prod) {
      setErreur('Date requise');
      return;
    }
    if (!form.operateurs) {
      setErreur('Veuillez sélectionner les opérateurs');
      return;
    }
    setLoading(true);
    setErreur('');
    try {
      const result = await api.post('/ribs/batches', {
        ...form,
        nom          : form.nom || undefined,
        prix_kg:         parseFloat(form.prix_kg) || 0,
        poids_cru_kg:    parseFloat(form.poids_cru_kg) || null,
        prix_vente_kg:   parseFloat(form.prix_vente_kg) || null,
        prix_vente_unite: parseFloat(form.prix_vente_unite) || null,
        mode_prix_vente: form.mode_prix_vente || 'kg',
      });
      const batch = await api.get(`/ribs/batches/${result.id}`);
      onBatchCree(batch);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur création batch');
    } finally {
      setLoading(false);
    }
  };

  const ouvrirBatch = async (id) => {
    try {
      const batch = await api.get(`/ribs/batches/${id}`);
      onBatchCree(batch);
    } catch {
      setErreur('Impossible de charger ce batch');
    }
  };

  return (
    <div className="etape-content">
      {batchASupprimer && (
        <ConfirmDeleteModal
          nomBatch={batchASupprimer.nom}
          loading={suppLoading}
          onConfirm={supprimerBatch}
          onAnnuler={() => setBatchASupprimer(null)}
        />
      )}
      {batchAnalytique && (
        <div className="modal-overlay" onClick={() => setBatchAnalytique(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 860, maxHeight: '90vh', overflowY: 'auto' }}>
            <BatchAnalytiquePanel batch={batchAnalytique} onFermer={() => setBatchAnalytique(null)} />
          </div>
        </div>
      )}
      {toast && <div className="toast-success">{toast}</div>}

      <div className="etape-header">
        <h2>Batch de production</h2>
        <p className="muted">Crée un nouveau batch ou reprends un batch existant.</p>
      </div>

      <div className="batch-toggle">
        <button
          className={`btn ${mode === 'nouveau' ? 'btn-primary' : 'btn-ghost'}`}
          onClick={() => setMode('nouveau')}
        >
          + Nouveau batch
        </button>
        <button
          className={`btn ${mode === 'liste' ? 'btn-primary' : 'btn-ghost'}`}
          onClick={() => setMode('liste')}
        >
          Reprendre un batch
        </button>
      </div>

      {mode === 'nouveau' && (
        <div className="card form-card">
          <RecetteStockPanel produit="cotes_levees" />
          <div className="form-grid">
            <div className="form-field">
              <label>Numéro du batch</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  value={form.nom}
                  onChange={e => setForm({ ...form, nom: e.target.value })}
                  readOnly={!nomManuel}
                  style={{ flex: 1, opacity: nomManuel ? 1 : 0.75, background: nomManuel ? undefined : 'rgba(255,255,255,0.04)' }}
                  placeholder={nomAuto || 'Chargement…'}
                />
                <button type="button" className="btn btn-ghost btn--sm"
                  onClick={() => setNomManuel(m => !m)} style={{ whiteSpace: 'nowrap' }}>
                  {nomManuel
                    ? <><Icon name="retour" size={13} /> Auto</>
                    : <><Icon name="modifier" size={13} /> Modifier</>
                  }
                </button>
              </div>
              <span className="muted" style={{ fontSize: 12 }}>Numéro auto-généré</span>
            </div>
            <div className="form-field">
              <label>Date de production *</label>
              <DateInput
                value={form.date_prod}
                onChange={v => setForm({ ...form, date_prod: v })}
              />
            </div>
            <div className="form-field">
              <label>Opérateurs *</label>
              <select
                value={form.operateurs}
                onChange={e => setForm({ ...form, operateurs: e.target.value })}
                required
              >
                <option value="">— Sélectionner —</option>
                <option value="LCU">LCU</option>
                <option value="ELA">ELA</option>
                <option value="LCU+ELA">Les deux</option>
              </select>
            </div>
            <div className="form-field">
              <label>Fournisseur</label>
              <input
                value={form.fournisseur}
                onChange={e => setForm({ ...form, fournisseur: e.target.value })}
                placeholder="ex: Viandex"
              />
            </div>
          </div>

          {/* BLOC ACHAT */}
          <div style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            padding: '12px 14px',
            marginTop: 12,
          }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: 'var(--muted)' }}>
              Prix d'achat matière (cru)
            </div>
            <div className="form-grid">
              <div className="form-field" style={{ maxWidth: 200 }}>
                <label>Prix d'achat matière (cru) — $/kg</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.prix_kg}
                  onChange={e => setForm({ ...form, prix_kg: e.target.value })}
                  placeholder="ex: 12.50"
                />
              </div>
              <div className="form-field" style={{ maxWidth: 200 }}>
                <label>Poids cru acheté (kg)</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.poids_cru_kg}
                  onChange={e => setForm({ ...form, poids_cru_kg: e.target.value })}
                  placeholder="ex: 15.0"
                />
                <span className="muted" style={{ fontSize: 11 }}>
                  Pour calculer le yield et déduire l'inventaire en kg
                </span>
              </div>
            </div>
          </div>

          {/* BLOC VENTE */}
          <BlocPrixVente
            mode={form.mode_prix_vente}
            prixKg={form.prix_vente_kg}
            prixUnite={form.prix_vente_unite}
            onModeChange={m => setForm({ ...form, mode_prix_vente: m })}
            onPrixKgChange={v => setForm({ ...form, prix_vente_kg: v })}
            onPrixUniteChange={v => setForm({ ...form, prix_vente_unite: v })}
            hasToggle={true}
            verrouille={false}
            labelUnite="Prix de vente à l'unité ($)"
          />

          <div className="form-field" style={{ marginTop: 12 }}>
            <label>Notes</label>
            <textarea
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              placeholder="Observations, qualité, etc."
              rows={2}
            />
          </div>
          {erreur && <p className="erreur-msg">{erreur}</p>}
          <button className="btn btn-primary btn-lg" onClick={creerBatch} disabled={loading || !form.operateurs}>
            {loading ? 'Création...' : 'Créer le batch →'}
          </button>
        </div>
      )}

      {mode === 'liste' && (
        <div className="card">
          {batches.length === 0
            ? <p className="muted empty-msg">Aucun batch côtes levées existant.</p>
            : (
              <table className="esc-table">
                <thead>
                  <tr>
                    <th>Batch</th>
                    <th>Date</th>
                    <th>Fournisseur</th>
                    <th>$/kg</th>
                    <th>Paires</th>
                    <th>Statut</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {batches.map(b => (
                    <tr key={b.id}>
                      <td className="font-medium">{b.nom}</td>
                      <td className="muted">{b.date_prod}</td>
                      <td className="muted">{b.fournisseur || '—'}</td>
                      <td className="gold">{b.prix_kg ? `${b.prix_kg}$` : '—'}</td>
                      <td>{b.nb_paires}</td>
                      <td>
                        <span className={`badge ${b.statut === 'finalise' ? 'badge--success' : 'badge--warning'}`}>
                          {b.statut === 'finalise' ? 'Finalisé' : 'En cours'}
                        </span>
                        {b.nb_tracabilite_manquante > 0 && (
                          <span className="badge-tracabilite-warning" style={{ marginLeft: 6 }}>
                            <Icon name="attention" size={11} /> {b.nb_tracabilite_manquante} sans facture
                          </span>
                        )}
                      </td>
                      <td className="td-actions">
                        <button className="btn btn-ghost btn--sm" onClick={() => ouvrirBatch(b.id)}>
                          Ouvrir
                        </button>
                        {b.statut === 'finalise' && (
                          <Link className="btn btn-ghost btn--sm" to={`/batches/${b.id}/tracabilite`}>
                            Traçabilité
                          </Link>
                        )}
                        {b.statut === 'finalise' && (
                          <button
                            className="btn btn-ghost btn--sm"
                            onClick={() => telechargerPdf(b)}
                            disabled={pdfLoadingId === b.id}
                            title="Télécharger la liste des paires (PDF)"
                          >
                            <Icon name="telecharger" size={14} />
                            {pdfLoadingId === b.id ? '...' : 'PDF'}
                          </button>
                        )}
                        {b.statut === 'finalise' && (
                          <button className="btn btn-ghost btn--sm" onClick={() => setBatchAnalytique(b)} title="Analytique">
                            <Icon name="analytics" size={14} />
                          </button>
                        )}
                        <button
                          className="btn btn-danger btn--sm btn-icon"
                          onClick={() => setBatchASupprimer(b)}
                          title="Supprimer ce batch"
                        >
                          <Icon name="fermer" size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          }
        </div>
      )}
    </div>
  );
}

// ─── PANNEAU TIMER CUISSON RIBS ──────────────

function PanneauTimerCuissonRibs({ batch, onRefresh }) {
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur]         = useState('');
  const [tempsEcoule, setTempsEcoule] = useState('');
  const [heureManuelle, setHeureManuelle] = useState('');

  useEffect(() => {
    if (!batch.heure_debut || batch.heure_fin) return;
    const calculer = () => {
      const ms = new Date() - new Date(batch.heure_debut);
      if (ms < 0) return;
      setTempsEcoule(`${Math.floor(ms / 3600000)}h ${String(Math.floor((ms % 3600000) / 60000)).padStart(2, '0')}min`);
    };
    calculer();
    const iv = setInterval(calculer, 30000);
    return () => clearInterval(iv);
  }, [batch.heure_debut, batch.heure_fin]);

  const fmtH = (iso) => iso ? new Date(iso).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' }) : '—';
  const duree = (d, f) => {
    if (!d || !f) return '';
    const ms = new Date(f) - new Date(d);
    if (ms < 0) return '';
    return `${Math.floor(ms / 3600000)}h ${String(Math.floor((ms % 3600000) / 60000)).padStart(2, '0')}min`;
  };

  const action = async (type, extra) => {
    setChargement(true); setErreur('');
    try {
      await api.patch(`/batches/${batch.id}/cuisson/${type}`, extra || {});
      onRefresh();
    } catch (e) { setErreur(e.response?.data?.error || 'Erreur'); }
    finally { setChargement(false); }
  };

  return (
    <div className="card timer-cuisson-panel" style={{ marginBottom: 16 }}>
      <div className="timer-cuisson-header">
        <Icon name="timer" size={16} color="var(--muted)" />
        <span className="timer-cuisson-title">Suivi cuisson</span>
      </div>
      {erreur && (
        <div className="error-inline" style={{ marginBottom: 12 }}>
          <Icon name="erreur" size={15} />{erreur}
        </div>
      )}
      {!batch.heure_debut && (
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <button className="btn btn-primary" onClick={() => action('demarrer')} disabled={chargement}>
            <Icon name="demarrer" size={15} />
            {chargement ? 'Démarrage...' : 'Démarrer la cuisson'}
          </button>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <div className="field" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: 11 }}>Ou heure manuelle</label>
              <input type="time" value={heureManuelle} onChange={e => setHeureManuelle(e.target.value)} style={{ width: 130 }} />
            </div>
            {heureManuelle && (
              <button className="btn btn-ghost btn--sm" disabled={chargement}
                onClick={() => action('demarrer', { heure_debut: new Date(`${batch.date_prod}T${heureManuelle}:00`).toISOString() })}>
                Enregistrer
              </button>
            )}
          </div>
        </div>
      )}
      {batch.heure_debut && !batch.heure_fin && (
        <div>
          <div style={{ display: 'flex', gap: 16, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <span className="timer-running-badge">
              <Icon name="demarrer" size={13} />
              Démarré à {fmtH(batch.heure_debut)}
            </span>
            {tempsEcoule && <span className="timer-elapsed">{tempsEcoule}</span>}
          </div>
          <button className="btn btn-danger" onClick={() => action('arreter')} disabled={chargement}>
            <Icon name="arreter" size={15} />
            {chargement ? 'Arrêt...' : 'Arrêter la cuisson'}
          </button>
        </div>
      )}
      {batch.heure_debut && batch.heure_fin && (
        <div className="timer-done-info">
          <span className="muted">Démarré {fmtH(batch.heure_debut)} → Terminé {fmtH(batch.heure_fin)}</span>
          <span className="gold" style={{ fontWeight: 600, fontFamily: 'Cinzel, serif', fontSize: 14 }}>
            Durée : {duree(batch.heure_debut, batch.heure_fin)}
          </span>
        </div>
      )}
    </div>
  );
}

// ─── ÉTAPE 1 BIS : ÉDITER UN BATCH EN COURS ──
// Affichée quand on revient à l'étape 1 alors qu'un batch est sélectionné.
// Permet de modifier les infos du batch (prix, fournisseur, etc.) sans recréer.

function EtapeBatchEditer({ batch, setBatch }) {
  const isFinalise = batch.statut === 'finalise';
  const [form, setForm] = useState({
    date_prod        : batch.date_prod || '',
    operateurs       : batch.operateurs || '',
    fournisseur      : batch.fournisseur || '',
    prix_kg          : batch.prix_kg || '',
    poids_cru_kg     : batch.poids_cru_kg || '',
    mode_prix_vente  : batch.mode_prix_vente || 'kg',
    prix_vente_kg    : batch.prix_vente_kg || '',
    prix_vente_unite : batch.prix_vente_unite || '',
    notes            : batch.notes || '',
  });
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur]   = useState('');
  const [toast, setToast]     = useState('');

  const enregistrer = async () => {
    setLoading(true);
    setErreur('');
    setToast('');
    try {
      const updated = await api.put(`/ribs/batches/${batch.id}`, {
        date_prod        : form.date_prod || null,
        operateurs       : form.operateurs || null,
        fournisseur      : form.fournisseur || null,
        prix_kg          : parseFloat(form.prix_kg) || null,
        poids_cru_kg     : parseFloat(form.poids_cru_kg) || null,
        mode_prix_vente  : form.mode_prix_vente,
        prix_vente_kg    : parseFloat(form.prix_vente_kg) || null,
        prix_vente_unite : parseFloat(form.prix_vente_unite) || null,
        notes            : form.notes || null,
      });
      setBatch(updated);
      setToast('Modifications enregistrées ✓');
      setTimeout(() => setToast(''), 3000);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur enregistrement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card form-card">
      <div className="card-section-header">
        <Icon name="modifier" size={15} color="var(--muted)" />
        <span className="form-titre">Modifier les informations — {batch.nom}</span>
        {isFinalise && <span className="badge badge--success">Finalisé</span>}
      </div>
      {erreur && <div className="error-inline" style={{ marginBottom: 12 }}><Icon name="erreur" size={15} />{erreur}</div>}
      {toast && <p className="success" style={{ marginBottom: 12 }}><Icon name="succes" size={14} /> {toast}</p>}

      <div className="form-grid">
        <div className="form-field">
          <label>Date de production</label>
          <DateInput value={form.date_prod} onChange={v => setForm({ ...form, date_prod: v })} />
        </div>
        <div className="form-field">
          <label>Opérateurs</label>
          <select
            value={form.operateurs}
            onChange={e => setForm({ ...form, operateurs: e.target.value })}
          >
            <option value="">— Sélectionner —</option>
            <option value="LCU">LCU</option>
            <option value="ELA">ELA</option>
            <option value="LCU+ELA">Les deux</option>
          </select>
        </div>
        <div className="form-field">
          <label>Fournisseur</label>
          <input
            value={form.fournisseur}
            onChange={e => setForm({ ...form, fournisseur: e.target.value })}
            placeholder="ex: Viandex"
          />
        </div>
        <div className="form-field">
          <label>Prix d'achat matière ($/kg)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={form.prix_kg}
            onChange={e => setForm({ ...form, prix_kg: e.target.value })}
            placeholder="ex: 12.50"
          />
        </div>
        <div className="form-field">
          <label>Poids cru acheté (kg)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={form.poids_cru_kg}
            onChange={e => setForm({ ...form, poids_cru_kg: e.target.value })}
            placeholder="ex: 15.0"
          />
          <span className="muted" style={{ fontSize: 11 }}>
            Pour calculer le yield et déduire l'inventaire en kg
          </span>
        </div>
      </div>

      <BlocPrixVente
        mode={form.mode_prix_vente}
        prixKg={form.prix_vente_kg}
        prixUnite={form.prix_vente_unite}
        verrouille={isFinalise}
        onModeChange={m => setForm({ ...form, mode_prix_vente: m })}
        onPrixKgChange={v => setForm({ ...form, prix_vente_kg: v })}
        onPrixUniteChange={v => setForm({ ...form, prix_vente_unite: v })}
      />

      <div className="form-field" style={{ marginTop: 12 }}>
        <label>Notes</label>
        <textarea
          rows="2"
          value={form.notes}
          onChange={e => setForm({ ...form, notes: e.target.value })}
        />
      </div>

      <div className="form-actions" style={{ marginTop: 16 }}>
        <button
          className="btn btn-primary"
          onClick={enregistrer}
          disabled={loading}
        >
          {loading ? 'Enregistrement...' : 'Enregistrer les modifications'}
        </button>
      </div>
      {isFinalise && (
        <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
          Batch finalisé : les prix de vente sont verrouillés. Date, opérateurs, fournisseur, prix d'achat et notes restent éditables.
        </p>
      )}
    </div>
  );
}

// ─── ÉTAPE 2 : SAISIE ────────────────────────

function EtapeSaisie({ batch, setBatch, onSuivant }) {
  const [demiA, setDemiA] = useState('');
  const [demiB, setDemiB] = useState('');
  const [erreur, setErreur] = useState('');
  const [succes, setSucces] = useState('');
  const [loading, setLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const demiARef = useRef(null);
  const importRef = useRef(null);

  useEffect(() => {
    demiARef.current?.focus();
  }, []);

  const ajouterPaire = async () => {
    const a = parseFloat(demiA);
    const b = parseFloat(demiB);
    if (!demiA || isNaN(a) || a <= 0 || !demiB || isNaN(b) || b <= 0) {
      setErreur('Les deux poids sont requis');
      return;
    }
    setDemiA('');
    setDemiB('');
    setErreur('');
    setLoading(true);
    try {
      const result = await api.post(`/ribs/batches/${batch.id}/paires`, {
        demi_a_g: a,
        demi_b_g: b,
      });
      setSucces(`Paire #${result.numero} — ${result.total_g}g ajoutée ✓`);
      setTimeout(() => setSucces(''), 2500);
      const updated = await api.get(`/ribs/batches/${batch.id}`);
      setBatch(updated);
      demiARef.current?.focus();
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur ajout paire');
    } finally {
      setLoading(false);
    }
  };

  const supprimerPaire = async (id) => {
    try {
      await api.delete(`/ribs/paires/${id}`);
      const updated = await api.get(`/ribs/batches/${batch.id}`);
      setBatch(updated);
    } catch {
      setErreur('Erreur suppression');
    }
  };

  const viderTout = async () => {
    if (!window.confirm('Supprimer toutes les paires de ce batch ? Cette action est irréversible.')) return;
    try {
      await api.delete(`/ribs/batches/${batch.id}/paires`);
      const updated = await api.get(`/ribs/batches/${batch.id}`);
      setBatch(updated);
      setSucces('Toutes les paires ont été supprimées.');
      setTimeout(() => setSucces(''), 3000);
    } catch {
      setErreur('Erreur lors de la suppression');
    }
  };

  const exporterCSV = () => {
    const paires = batch.paires || [];
    const lignes = ['demi_a_g,demi_b_g', ...paires.map(p => `${p.demi_a_g},${p.demi_b_g}`)];
    const blob = new Blob([lignes.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `paires-${batch.nom.replace(/\s+/g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importerCSV = async (e) => {
    const fichier = e.target.files[0];
    if (!fichier) return;
    e.target.value = '';
    const texte = await fichier.text();
    const lignes = texte.trim().split('\n').filter(Boolean);
    const debut = lignes[0].toLowerCase().includes('demi') ? 1 : 0;
    const paires = lignes.slice(debut).map(l => {
      const [demi_a_g, demi_b_g] = l.split(',').map(s => s.trim());
      return { demi_a_g, demi_b_g };
    });
    if (paires.length === 0) {
      setErreur('Fichier CSV vide ou invalide');
      return;
    }
    setImportLoading(true);
    setErreur('');
    try {
      const result = await api.post(`/ribs/batches/${batch.id}/paires/import`, { paires });
      if (result.erreurs.length > 0) {
        setErreur(`${result.importes} importée(s), ${result.erreurs.length} erreur(s) : ${result.erreurs[0]}`);
      } else {
        setSucces(`${result.importes} paire(s) importée(s) ✓`);
        setTimeout(() => setSucces(''), 3000);
      }
      const updated = await api.get(`/ribs/batches/${batch.id}`);
      setBatch(updated);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur import CSV');
    } finally {
      setImportLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !loading) ajouterPaire();
  };

  const paires = batch.paires || [];
  const totalPoids = paires.reduce((s, p) => s + p.total_g, 0);
  const valA = parseFloat(demiA);
  const valB = parseFloat(demiB);
  const modePrixVente = batch.mode_prix_vente || 'kg';
  const prixVenteKg = parseFloat(batch.prix_vente_kg) || 0;
  const prixVenteUnite = parseFloat(batch.prix_vente_unite) || 0;
  const totalLiveVente = modePrixVente === 'kg' && prixVenteKg > 0
    ? Math.round((totalPoids / 1000) * prixVenteKg * 100) / 100
    : null;

  return (
    <div className="etape-content">
      <div className="etape-header">
        <h2>Saisie des paires</h2>
        <p className="muted">
          Batch : <strong className="gold">{batch.nom}</strong>
          {batch.prix_kg > 0 && <> · Achat : {batch.prix_kg}$/kg</>}
          {batch.prix_vente_kg > 0 && batch.mode_prix_vente === 'kg' && <> · Vente : {batch.prix_vente_kg}$/kg</>}
          {batch.prix_vente_unite > 0 && batch.mode_prix_vente === 'unite' && <> · Vente : {batch.prix_vente_unite}$/unité</>}
        </p>
      </div>

      <div className="raccourcis card">
        <span className="muted" style={{ fontSize: 12 }}>Appuie sur Entrée pour ajouter la paire</span>
        <kbd>Entrée</kbd>
        <div className="csv-actions">
          <button
            className="btn btn-ghost btn--sm"
            onClick={exporterCSV}
            disabled={paires.length === 0}
          >
            <Icon name="telecharger" size={13} /> Exporter CSV
          </button>
          <button
            className="btn btn-ghost btn--sm"
            onClick={() => importRef.current?.click()}
            disabled={importLoading}
          >
            <Icon name="televerser" size={13} /> {importLoading ? 'Import...' : 'Importer CSV'}
          </button>
          <button
            className="btn btn-danger btn--sm"
            onClick={viderTout}
            disabled={paires.length === 0}
          >
            <Icon name="fermer" size={13} /> Vider tout
          </button>
          <input
            ref={importRef}
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={importerCSV}
          />
        </div>
      </div>

      <div className="card saisie-form">
        <div className="paire-inputs">
          <div className="form-field">
            <label>Moitié A (g)</label>
            <input
              ref={demiARef}
              type="number"
              className="poids-input"
              value={demiA}
              onChange={e => setDemiA(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Poids moitié A"
              step="0.1"
              min="0"
            />
          </div>
          <div className="form-field">
            <label>Moitié B (g)</label>
            <input
              type="number"
              className="poids-input"
              value={demiB}
              onChange={e => setDemiB(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Poids moitié B"
              step="0.1"
              min="0"
            />
          </div>
          {demiA && demiB && !isNaN(valA) && !isNaN(valB) && valA > 0 && valB > 0 && (
            <div className="total-preview">
              <span className="muted" style={{ fontSize: 11 }}>Total</span>
              <span className="gold" style={{ fontSize: 22, fontWeight: 700 }}>
                {(valA + valB).toFixed(0)}g
              </span>
            </div>
          )}
        </div>
        <button
          className="btn btn-primary btn-lg"
          onClick={ajouterPaire}
          disabled={loading}
          style={{ marginTop: 12 }}
        >
          {loading ? 'Ajout...' : 'Ajouter la paire'}
        </button>
        {succes && <div className="succes-msg">{succes}</div>}
        {erreur && <div className="erreur-msg">{erreur}</div>}
      </div>

      <div className="stats-row">
        <StatBadge label="Paires" value={paires.length} color="var(--accent)" />
        <StatBadge label="Poids total (g)" value={totalPoids.toFixed(0)} color="var(--muted)" />
        {paires.length > 0 && (
          <StatBadge
            label="Poids moyen (g)"
            value={(totalPoids / paires.length).toFixed(0)}
            color="var(--info)"
          />
        )}
        {totalLiveVente !== null && (
          <div className="total-vente-stat">
            <div className="rentabilite-label">Total vente estimé</div>
            <div className="rentabilite-valeur gold">{totalLiveVente.toFixed(2)} $</div>
          </div>
        )}
      </div>

      {paires.length > 0 && (
        <div className="card">
          <table className="esc-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Moitié A (g)</th>
                <th>Moitié B (g)</th>
                <th>Total (g)</th>
                {batch.prix_kg > 0 && <th className="muted">Coût achat</th>}
                {(prixVenteKg > 0 || prixVenteUnite > 0) && <th className="gold">Prix vente</th>}
                <th>Groupe</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {[...paires].reverse().map(p => {
                const prixVentePaire = modePrixVente === 'kg' && prixVenteKg > 0
                  ? Math.round((p.total_g / 1000) * prixVenteKg)
                  : (modePrixVente === 'unite' && prixVenteUnite > 0 ? prixVenteUnite : null);
                return (
                  <tr key={p.id}>
                    <td className="font-medium">#{p.numero}</td>
                    <td>{p.demi_a_g}g</td>
                    <td>{p.demi_b_g}g</td>
                    <td className="gold">{p.total_g}g</td>
                    {batch.prix_kg > 0 && (
                      <td className="muted" style={{ fontSize: 12 }}>
                        {((p.total_g / 1000) * batch.prix_kg).toFixed(2)}$
                      </td>
                    )}
                    {(prixVenteKg > 0 || prixVenteUnite > 0) && (
                      <td className="success">
                        {prixVentePaire !== null ? `${prixVentePaire.toFixed(2)}$` : '—'}
                      </td>
                    )}
                    <td>
                      {p.groupe
                        ? (
                          <span
                            className="badge"
                            style={{
                              background: `color-mix(in srgb, ${GROUPE_COULEURS[p.groupe] || 'var(--muted)'} 14%, transparent)`,
                              color: GROUPE_COULEURS[p.groupe] || 'var(--muted)',
                            }}
                          >
                            {p.groupe}
                          </span>
                        )
                        : <span className="muted">—</span>
                      }
                    </td>
                    <td>
                      <button
                        className="btn-icon danger"
                        onClick={() => supprimerPaire(p.id)}
                        title="Supprimer"
                      >
                        <Icon name="fermer" size={13} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Panneau play/stop cuisson ribs */}
      <PanneauTimerCuissonRibs batch={batch} onRefresh={async () => {
        const updated = await api.get(`/ribs/batches/${batch.id}`);
        setBatch(updated);
      }} />

      <CourbeCuissonBatch batchId={batch.id} produit="cotes_levees" sessionActive={true} />

      <div className="etape-actions">
        <button
          className="btn btn-primary btn-lg"
          onClick={onSuivant}
          disabled={paires.length === 0}
        >
          Créer les groupes →
        </button>
      </div>
    </div>
  );
}

// ─── ÉDITEUR PRIX DE VENTE (étape 3 Groupes) ─

function PrixVenteEditeurRibs({ batch, setBatch }) {
  const isFinalise = batch.statut === 'finalise';
  const [mode, setMode]            = useState(batch.mode_prix_vente || 'kg');
  const [prixKg, setPrixKg]        = useState(batch.prix_vente_kg || '');
  const [prixUnite, setPrixUnite]  = useState(batch.prix_vente_unite || '');
  const [loading, setLoading]      = useState(false);
  const [erreur, setErreur]        = useState('');
  const [toast, setToast]          = useState('');

  const dirty =
    mode !== (batch.mode_prix_vente || 'kg') ||
    String(prixKg)    !== String(batch.prix_vente_kg    || '') ||
    String(prixUnite) !== String(batch.prix_vente_unite || '');

  const enregistrer = async () => {
    setLoading(true);
    setErreur('');
    setToast('');
    try {
      const updated = await api.put(`/ribs/batches/${batch.id}`, {
        mode_prix_vente  : mode,
        prix_vente_kg    : parseFloat(prixKg)    || null,
        prix_vente_unite : parseFloat(prixUnite) || null,
      });
      setBatch(updated);
      setToast('Prix de vente enregistré ✓');
      setTimeout(() => setToast(''), 3000);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur enregistrement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card form-card" style={{ marginTop: 16 }}>
      <h3>Prix de vente</h3>
      {erreur && <p className="danger" style={{ marginBottom: 8 }}>{erreur}</p>}
      {toast && <p className="success" style={{ marginBottom: 8 }}>{toast}</p>}
      <BlocPrixVente
        mode={mode}
        prixKg={prixKg}
        prixUnite={prixUnite}
        verrouille={isFinalise}
        onModeChange={setMode}
        onPrixKgChange={setPrixKg}
        onPrixUniteChange={setPrixUnite}
      />
      <div className="form-actions" style={{ marginTop: 12 }}>
        <button
          className="btn btn-primary"
          onClick={enregistrer}
          disabled={loading || isFinalise || !dirty}
        >
          {loading ? 'Enregistrement...' : 'Enregistrer le prix'}
        </button>
        {isFinalise && (
          <span className="muted" style={{ marginLeft: 12, fontSize: 12 }}>
            Verrouillé après finalisation.
          </span>
        )}
      </div>
    </div>
  );
}

// ─── ÉTAPE 3 : GROUPES ───────────────────────

function EtapeGroupes({ batch, setBatch, onSuivant }) {
  // Initialisation : (1) config persistée du batch, (2) défaut global app_settings, (3) hardcoded
  const initialCfg = (() => {
    if (batch?.groupes_cfg_json) {
      try {
        const parsed = JSON.parse(batch.groupes_cfg_json);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed.map(g => ({ ...g }));
      } catch { /* fallback */ }
    }
    return GROUPES_DEFAUT.map(g => ({ ...g }));
  })();

  const [groupesCfg, setGroupesCfg] = useState(initialCfg);
  const [resultat, setResultat] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoLoading, setAutoLoading] = useState(false);
  const [erreur, setErreur] = useState('');

  // Si pas de config dans le batch, charger le défaut global (dernière config utilisée)
  useEffect(() => {
    if (batch?.groupes_cfg_json) return;
    api.get('/ribs/groupes-cfg-defaut')
      .then(r => {
        if (Array.isArray(r.cfg) && r.cfg.length > 0) {
          setGroupesCfg(r.cfg.map(g => ({ ...g })));
        }
      })
      .catch(() => {});
  }, [batch?.id, batch?.groupes_cfg_json]);

  const updateCfg = (idx, champ, val) => {
    setGroupesCfg(prev =>
      prev.map((g, i) => i === idx ? { ...g, [champ]: parseInt(val) || 0 } : g)
    );
  };

  const lancerGroupement = async (cfg = groupesCfg) => {
    for (const g of cfg) {
      if (g.min >= g.max) {
        setErreur(`Min doit être inférieur au Max pour le groupe ${g.nom}`);
        return;
      }
    }
    setLoading(true);
    setErreur('');
    try {
      const res = await api.post(`/ribs/batches/${batch.id}/grouper`, { groupes: cfg });
      setResultat(res);
      const updated = await api.get(`/ribs/batches/${batch.id}`);
      setBatch(updated);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur groupement');
    } finally {
      setLoading(false);
    }
  };

  const autoCalculer = async () => {
    setAutoLoading(true);
    setErreur('');
    try {
      const res = await api.post(`/ribs/batches/${batch.id}/grouper/auto`);
      setGroupesCfg(res.groupesCfg);
      await lancerGroupement(res.groupesCfg);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur calcul automatique');
    } finally {
      setAutoLoading(false);
    }
  };

  const exporterCSV = () => {
    const paires = batch.paires || [];
    const lignes = ['numero,demi_a_g,demi_b_g,total_g,groupe'];
    paires.forEach(p =>
      lignes.push(`${p.numero},${p.demi_a_g},${p.demi_b_g},${p.total_g},${p.groupe || ''}`)
    );
    const blob = new Blob([lignes.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `groupes-ribs-${batch.nom.replace(/\s+/g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const paires = batch.paires || [];

  return (
    <div className="etape-content">
      <div className="etape-header">
        <h2>Groupement par taille</h2>
        <p className="muted">
          Batch : <strong className="gold">{batch.nom}</strong> · {paires.length} paires
        </p>
      </div>

      <div className="card form-card">
        <div className="groupes-config">
          {groupesCfg.map((g, idx) => (
            <div key={g.nom} className="groupe-config-row">
              <span
                className="groupe-config-label"
                style={{ color: GROUPE_COULEURS[g.nom] }}
              >
                {g.nom}
              </span>
              <div className="form-field">
                <label>Min (g)</label>
                <input
                  type="number"
                  value={g.min}
                  onChange={e => updateCfg(idx, 'min', e.target.value)}
                />
              </div>
              <span className="muted" style={{ paddingBottom: 10 }}>—</span>
              <div className="form-field">
                <label>Max (g)</label>
                <input
                  type="number"
                  value={g.max}
                  onChange={e => updateCfg(idx, 'max', e.target.value)}
                />
              </div>
            </div>
          ))}
        </div>
        {erreur && <p className="erreur-msg">{erreur}</p>}
        <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
          <button
            className="btn btn-ghost"
            onClick={autoCalculer}
            disabled={autoLoading || paires.length < 3}
            title="Calcule automatiquement les plages par percentiles (33% / 66%)"
          >
            <Icon name="auto" size={14} /> {autoLoading ? 'Calcul...' : 'Auto-calculer (percentiles)'}
          </button>
          <button
            className="btn btn-primary btn-lg"
            onClick={() => lancerGroupement()}
            disabled={loading}
          >
            {loading ? 'Groupement...' : 'Grouper les paires'}
          </button>
        </div>
      </div>

      <PrixVenteEditeurRibs batch={batch} setBatch={setBatch} />

      {resultat && (
        <>
          <div className="stats-row">
            {resultat.sommaire.map(g => (
              <StatBadge
                key={g.nom}
                label={g.nom}
                value={g.count}
                color={GROUPE_COULEURS[g.nom] || 'var(--muted)'}
              />
            ))}
            {resultat.hors_plage > 0 && (
              <StatBadge label="Hors plage" value={resultat.hors_plage} color="var(--danger)" />
            )}
          </div>

          <div className="groupes-grid">
            {resultat.sommaire.filter(g => g.count > 0).map(g => {
              const pairesGroupe = paires.filter(p => p.groupe === g.nom);
              return (
                <div key={g.nom} className="groupe-card card">
                  <div className="groupe-header">
                    <span className="groupe-num" style={{ color: GROUPE_COULEURS[g.nom] }}>
                      {g.nom}
                    </span>
                    <span className="groupe-poids">{g.count} paires</span>
                  </div>
                  <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
                    Poids moyen : {g.poids_moyen_g}g
                  </div>
                  <table className="esc-table groupe-table">
                    <tbody>
                      {pairesGroupe.map(p => (
                        <tr key={p.id}>
                          <td className="font-medium">#{p.numero}</td>
                          <td className="muted">{p.demi_a_g}g / {p.demi_b_g}g</td>
                          <td className="gold">{p.total_g}g</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>

          {resultat.hors_plage > 0 && (
            <div className="card">
              <h3 style={{ marginBottom: 12, color: 'var(--danger)', fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icon name="attention" size={14} color="var(--danger)" /> Hors plage ({resultat.hors_plage})
              </h3>
              <div className="leftovers-list">
                {paires.filter(p => p.groupe === 'Hors plage').map(p => (
                  <span key={p.id} className="leftover-tag">
                    #{p.numero} — {p.total_g}g
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="etape-actions">
            <button className="btn btn-ghost" onClick={() => lancerGroupement()}>
              <Icon name="recalculer" size={14} /> Recalculer
            </button>
            <button
              className="btn btn-ghost"
              onClick={exporterCSV}
              title="Exporter les paires avec leur groupe"
            >
              <Icon name="telecharger" size={14} /> Exporter CSV
            </button>
            <button className="btn btn-primary btn-lg" onClick={onSuivant}>
              Finaliser le batch →
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── MODAL DE CONFIRMATION ───────────────────

function ModalFinalisation({ batch, resultat, onFermer, onRetour }) {
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfErreur, setPdfErreur] = useState('');

  const telechargerPDF = async () => {
    setPdfLoading(true);
    setPdfErreur('');
    try {
      const blob = await api.blob(`/ribs/batches/${batch.id}/pdf`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport-ribs-${batch.nom.replace(/\s+/g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setPdfErreur('Erreur téléchargement PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  // Affichage inline pleine largeur (avant : modale 500px qui tassait le contenu).
  return (
    <div className="card form-card finalisation-succes">
        <div className="modal-header">
          <div className="modal-success-icon">
            <Icon name="succes" size={32} weight="fill" />
          </div>
          <h2>Batch finalisé !</h2>
          <p className="muted">{batch.nom}</p>
        </div>

        <ul className="modal-checklist">
          <li className="checklist-item ok">
            <span className="checklist-icon"><Icon name="check" size={14} weight="bold" /></span>
            Batch marqué comme finalisé
          </li>
          <li className={`checklist-item ${resultat.nb_produits > 0 ? 'ok' : 'warn'}`}>
            <span className="checklist-icon">
              {resultat.nb_produits > 0 ? <Icon name="check" size={14} weight="bold" /> : <Icon name="attention" size={14} />}
            </span>
            {resultat.nb_produits} produit(s) créés dans l'inventaire
          </li>
          {resultat.wc_sync && (
            <li className={`checklist-item ${!resultat.wc_sync.configured ? 'warn' : resultat.wc_sync.errors.length > 0 ? 'warn' : 'ok'}`}>
              <span className="checklist-icon">
                {(!resultat.wc_sync.configured || resultat.wc_sync.errors.length > 0)
                  ? <Icon name="attention" size={14} />
                  : <Icon name="check" size={14} weight="bold" />
                }
              </span>
              {!resultat.wc_sync.configured
                ? 'WooCommerce non configuré'
                : resultat.wc_sync.errors.length > 0
                  ? `WooCommerce : ${resultat.wc_sync.success} ok, ${resultat.wc_sync.errors.length} erreur(s)`
                  : `WooCommerce : ${resultat.wc_sync.success} produit(s) synchronisé(s)`
              }
            </li>
          )}
        </ul>
        {resultat.wc_sync?.errors?.length > 0 && (
          <div style={{ marginTop: 10, padding: '8px 12px', background: 'color-mix(in srgb, var(--danger) 10%, transparent)', borderRadius: 'var(--radius-md)', border: '1px solid color-mix(in srgb, var(--danger) 25%, transparent)' }}>
            {resultat.wc_sync.errors.map((e, i) => (
              <p key={i} className="danger" style={{ fontSize: 12, margin: '2px 0', display: 'flex', alignItems: 'center', gap: 4 }}>
                <Icon name="attention" size={12} /> {e}
              </p>
            ))}
          </div>
        )}

        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={telechargerPDF} disabled={pdfLoading}>
            <Icon name="telecharger" size={14} /> {pdfLoading ? 'Génération...' : 'Télécharger PDF'}
          </button>
        </div>
        {pdfErreur && (
          <p className="erreur-msg" style={{ textAlign: 'center', marginTop: 10 }}>{pdfErreur}</p>
        )}

        {/* Stats yield / coût par paire — calcul rentabilité */}
        {resultat.analytique && (
          <div className="rentabilite-grid-card">
            <div className="rentabilite-grid">
              <div className="rentabilite-item">
                <div className="rentabilite-label">Coût matières / paire</div>
                <div className="rentabilite-valeur gold">
                  {resultat.analytique.cout_par_unite != null ? `${resultat.analytique.cout_par_unite.toFixed(2)} $` : '—'}
                </div>
              </div>
              <div className="rentabilite-item">
                <div className="rentabilite-label">Coût matières / kg</div>
                <div className="rentabilite-valeur gold">
                  {resultat.analytique.cout_par_kg != null ? `${resultat.analytique.cout_par_kg.toFixed(2)} $/kg` : '—'}
                </div>
              </div>
              <div className="rentabilite-item">
                <div className="rentabilite-label">Yield (cuit / cru)</div>
                <div className={`rentabilite-valeur ${resultat.analytique.yield_global_pct == null ? 'muted' : resultat.analytique.yield_global_pct >= 70 ? 'success' : resultat.analytique.yield_global_pct >= 55 ? 'warning' : 'danger'}`}>
                  {resultat.analytique.yield_global_pct != null ? `${resultat.analytique.yield_global_pct.toFixed(1)} %` : 'N/A'}
                </div>
                {resultat.analytique.poids_cru_kg && resultat.analytique.poids_total_kg != null && (
                  <div className="muted" style={{ fontSize: 11 }}>
                    {resultat.analytique.poids_total_kg} kg / {resultat.analytique.poids_cru_kg} kg
                  </div>
                )}
              </div>
              <div className="rentabilite-item">
                <div className="rentabilite-label">Paires produites</div>
                <div className="rentabilite-valeur gold">{resultat.analytique.nb_paires || 0}</div>
              </div>
            </div>
          </div>
        )}

        {resultat.deductions && resultat.deductions.length > 0 && (
          <div style={{ marginTop: 16, textAlign: 'left' }}>
            <h4 style={{ marginBottom: 10 }}>Inventaire déduit</h4>
            <table className="esc-table">
              <thead>
                <tr><th>Ingrédient</th><th>Déduit</th><th>Stock restant</th></tr>
              </thead>
              <tbody>
                {resultat.deductions.map(d => (
                  <tr key={d.matiere_id}>
                    <td>{d.nom}</td>
                    <td className="gold">−{d.quantite_deduite} {d.unite}</td>
                    <td className={d.statut === 'negatif' ? 'danger' : d.statut === 'bas' ? 'warning' : 'success'}>
                      {d.stock_apres} {d.unite}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {resultat.deductions.some(d => d.statut !== 'ok') && (
              <p className="warning" style={{ marginTop: 8, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icon name="attention" size={14} /> {resultat.deductions.filter(d => d.statut !== 'ok').length} ingrédient(s) à réapprovisionner
              </p>
            )}
          </div>
        )}

        <CoutRevient analytique={resultat.analytique} />

        <CourbeCuissonBatch batchId={batch.id} produit="cotes_levees" sessionActive={false} />

        <div className="modal-footer">
          <button className="btn btn-primary btn-lg" onClick={onRetour}>
            <Icon name="retour" size={14} /> Retour aux batches
          </button>
        </div>
    </div>
  );
}

// ─── ÉTAPE 4 : FINALISER ─────────────────────

function EtapeFinaliser({ batch, onFinalise }) {
  const isFinalise = batch.statut === 'finalise';
  const [prixKg, setPrixKg] = useState(batch.prix_kg || '');
  const [modePrixVente, setModePrixVente] = useState(batch.mode_prix_vente || 'kg');
  const [prixVenteKg, setPrixVenteKg] = useState(batch.prix_vente_kg || '');
  const [prixVenteUnite, setPrixVenteUnite] = useState(batch.prix_vente_unite || '');
  const [modeWC, setModeWC] = useState('grouped'); // 'grouped' | 'per_pair'
  const [loading, setLoading] = useState(false);
  const [recalcLoading, setRecalcLoading] = useState(false);
  const [erreur, setErreur] = useState('');
  const [resultat, setResultat] = useState(null);
  const [apercuDeduction, setApercuDeduction] = useState(null);
  const [finalForm, setFinalForm] = useState(() => {
    const defaut = creerFormulaireTraiteFinal('cotes_levees');
    return {
      ...defaut,
      date_congelation: batch.date_congelation || defaut.date_congelation,
      date_expiration_produit: batch.date_expiration_produit || defaut.date_expiration_produit,
      temp_conservation: batch.temp_conservation || defaut.temp_conservation,
      incident: batch.incident || '',
    };
  });
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfErreur, setPdfErreur] = useState('');
  const [poidsCruInput, setPoidsCruInput] = useState(batch.poids_cru_kg || '');
  const [poidsCruSaving, setPoidsCruSaving] = useState(false);
  const [poidsCruSauve, setPoidsCruSauve] = useState(parseFloat(batch.poids_cru_kg) || null);

  const reloadApercu = () => {
    api.get(`/ribs/batches/${batch.id}/apercu-deduction`)
      .then(setApercuDeduction)
      .catch(() => {});
  };

  useEffect(() => {
    if (batch.statut !== 'finalise') {
      reloadApercu();
    }
  }, []); // eslint-disable-line

  const sauverPoidsCru = async () => {
    const val = parseFloat(poidsCruInput);
    const newVal = (Number.isFinite(val) && val > 0) ? val : null;
    if (newVal === poidsCruSauve) return;
    setPoidsCruSaving(true);
    try {
      await api.put(`/ribs/batches/${batch.id}`, { poids_cru_kg: newVal });
      setPoidsCruSauve(newVal);
      reloadApercu();
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur enregistrement poids cru');
    } finally {
      setPoidsCruSaving(false);
    }
  };

  const recalculerPrix = async () => {
    setRecalcLoading(true);
    setErreur('');
    try {
      await api.post(`/ribs/batches/${batch.id}/recalculer-prix-ventes`);
      setErreur('');
      // On force un rechargement visuel (le batch sera reload via EtapeSaisie si on revient)
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur lors du recalcul');
    } finally {
      setRecalcLoading(false);
    }
  };

  const paires = batch.paires || [];
  const pairesGroupees = paires.filter(p => p.groupe && p.groupe !== 'Hors plage');
  const horsPlage = paires.filter(p => p.groupe === 'Hors plage' || !p.groupe);
  const prixKgNum = parseFloat(prixKg) || 0;
  const prixVenteKgNum = parseFloat(prixVenteKg) || 0;
  const prixVenteUniteNum = parseFloat(prixVenteUnite) || 0;
  const batchCode = batch.nom.replace(/\s+/g, '').toUpperCase();

  // Total live en mode $/kg
  const totalPoidsGroupeG = pairesGroupees.reduce((s, p) => s + p.total_g, 0);
  const totalLiveVente = modePrixVente === 'kg' && prixVenteKgNum > 0
    ? Math.round((totalPoidsGroupeG / 1000) * prixVenteKgNum * 100) / 100
    : null;

  const groupesSommaire = ['Small', 'Medium', 'Large'].map(nom => {
    const subset = pairesGroupees.filter(p => p.groupe === nom);
    if (subset.length === 0) return null;
    const poidsMoyen = subset.reduce((s, p) => s + p.total_g, 0) / subset.length;
    // Prix de vente (pas prix d'achat)
    const prixVente = modePrixVente === 'kg'
      ? Math.round((poidsMoyen / 1000) * prixVenteKgNum)
      : prixVenteUniteNum;
    return {
      nom,
      count: subset.length,
      poidsMoyen: Math.round(poidsMoyen),
      prix: prixVente,
      sku: `RIB-${batchCode}-${nom.toUpperCase()}`,
    };
  }).filter(Boolean);

  const exporterWooCommerce = () => {
    const descLongue = `Côtes levées style St-Louis fumées lentement au bois d'érable du Québec. Sans MSG ni nitrite. Préparées par Escamel Artisans du Fumoir.`;
    const imageUrl = 'https://escamel.ca/wp-content/uploads/2025/04/IMG_3883-1-scaled.jpg';
    const headers = [
      'Name', 'Type', 'Published', 'Regular price', 'Categories',
      'SKU', 'Weight (kg)', 'Short description', 'Description',
      'Stock status', 'Stock', 'Sold individually?', 'Images', 'Catalog visibility',
    ];
    const echapper = (val) => {
      const s = String(val ?? '');
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lignes = [headers.join(',')];

    if (modeWC === 'grouped') {
      groupesSommaire.forEach(g => {
        const row = [
          `Côtes levées — Format ${g.nom} (${batch.nom})`,
          'simple', '1',
          g.prix > 0 ? g.prix : '',
          'Côtes Levées', g.sku, (g.poidsMoyen / 1000).toFixed(3),
          `Format ${g.nom} — Poids moyen ${(g.poidsMoyen / 1000).toFixed(2)} kg`,
          descLongue, 'instock', g.count, '0', imageUrl, 'visible',
        ].map(echapper);
        lignes.push(row.join(','));
      });
    } else {
      pairesGroupees.forEach(p => {
        const prix = modePrixVente === 'kg'
          ? Math.round((p.total_g / 1000) * prixVenteKgNum)
          : prixVenteUniteNum;
        const sku = `RIB-${batchCode}-P${String(p.numero).padStart(3, '0')}`;
        const row = [
          `Côtes levées — Paire #${p.numero} (${(p.total_g / 1000).toFixed(2)}kg)`,
          'simple', '1',
          prix > 0 ? prix : '',
          'Côtes Levées', sku, (p.total_g / 1000).toFixed(3),
          `${p.groupe} — Moitié A : ${p.demi_a_g}g, Moitié B : ${p.demi_b_g}g, Total : ${p.total_g}g`,
          descLongue, 'instock', '1', '1', imageUrl, 'visible',
        ].map(echapper);
        lignes.push(row.join(','));
      });
    }

    const blob = new Blob([lignes.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `woocommerce_import-ribs-${batch.nom.replace(/\s+/g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const telechargerPDF = async () => {
    setPdfLoading(true);
    setPdfErreur('');
    try {
      const blob = await api.blob(`/ribs/batches/${batch.id}/pdf`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport-ribs-${batch.nom.replace(/\s+/g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setPdfErreur('Erreur téléchargement PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const finaliser = async () => {
    if (pairesGroupees.length === 0) {
      setErreur("Aucune paire groupée — retourne à l'étape précédente");
      return;
    }
    setLoading(true);
    setErreur('');
    try {
      const res = await api.post(`/ribs/batches/${batch.id}/finaliser`, {
        prix_kg: prixKgNum,
        mode: modeWC,
        mode_prix_vente: modePrixVente,
        prix_vente_kg: prixVenteKgNum || null,
        prix_vente_unite: prixVenteUniteNum || null,
        ...finalForm,
      });
      setResultat(res);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur finalisation');
    } finally {
      setLoading(false);
    }
  };

  if (resultat) {
    return (
      <div className="etape-content">
        <ModalFinalisation
          batch={batch}
          resultat={resultat}
          onFermer={onFinalise}
          onRetour={onFinalise}
        />
      </div>
    );
  }

  return (
    <div className="etape-content">
      <div className="etape-header">
        <h2>Finaliser le batch</h2>
        <p className="muted">
          Batch : <strong className="gold">{batch.nom}</strong>
          · {pairesGroupees.length} paires groupées
          {horsPlage.length > 0 && <> · <span className="danger">{horsPlage.length} hors plage</span></>}
        </p>
      </div>

      <div className="card form-card">
        {/* BLOC ACHAT */}
        <div style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: '12px 14px',
          marginBottom: 12,
        }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: 'var(--muted)' }}>
            Prix d'achat matière (cru)
          </div>
          <div className="form-field" style={{ maxWidth: 200 }}>
            <label>
              Prix d'achat matière (cru) — $/kg
              {isFinalise && (
                <span
                  title="Finalisé — non modifiable (impact comptable/stock)"
                  style={{ marginLeft: 6, fontSize: 11, color: 'var(--muted)', display: 'inline-flex', alignItems: 'center', gap: 3 }}
                >
                  <Icon name="verrou" size={11} /> Verrouillé
                </span>
              )}
            </label>
            <input
              type="number"
              step="0.01"
              value={prixKg}
              onChange={e => setPrixKg(e.target.value)}
              disabled={isFinalise}
              placeholder="ex: 12.50"
              style={{ opacity: isFinalise ? 0.5 : 1 }}
            />
          </div>
        </div>

        {/* BLOC VENTE */}
        <BlocPrixVente
          mode={modePrixVente}
          prixKg={prixVenteKg}
          prixUnite={prixVenteUnite}
          onModeChange={setModePrixVente}
          onPrixKgChange={setPrixVenteKg}
          onPrixUniteChange={setPrixVenteUnite}
          hasToggle={true}
          verrouille={isFinalise}
          poidsTotal={totalPoidsGroupeG}
          labelUnite="Prix de vente à l'unité ($)"
        />

        {/* Bouton recalculer (pré-finalisation seulement) */}
        {!isFinalise && pairesGroupees.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <button
              type="button"
              className="btn btn-ghost btn--sm"
              onClick={recalculerPrix}
              disabled={recalcLoading}
              title="Recalcule et grave le prix sur toutes les paires selon le mode et prix actuels"
            >
              <Icon name="recalculer" size={13} /> {recalcLoading ? 'Recalcul...' : 'Recalculer les prix gravés'}
            </button>
          </div>
        )}

        <div className="form-field" style={{ marginTop: 14 }}>
          <label>Mode d'export WooCommerce</label>
          <select value={modeWC} onChange={e => setModeWC(e.target.value)}>
            <option value="grouped">Groupé — 3 produits (Small / Medium / Large)</option>
            <option value="per_pair">Par paire — 1 produit par paire</option>
          </select>
        </div>
      </div>

      {/* Aperçu des produits */}
      <SectionProduitFini
        produit="cotes_levees"
        form={finalForm}
        setForm={setFinalForm}
        batchId={batch.id}
        batchStatut={batch.statut}
      />

      {(modeWC === 'grouped' ? groupesSommaire : pairesGroupees).length > 0 && (
        <div className="card">
          <h3 className="card-title" style={{ marginBottom: 14 }}>
            Aperçu des produits à créer
          </h3>
          {modeWC === 'grouped' ? (
            <table className="esc-table">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Groupe</th>
                  <th>Quantité</th>
                  <th>Poids moyen</th>
                  <th>Prix de vente</th>
                </tr>
              </thead>
              <tbody>
                {groupesSommaire.map(g => (
                  <tr key={g.nom}>
                    <td className="font-medium gold">{g.sku}</td>
                    <td>
                      <span
                        className="badge"
                        style={{
                          background: `color-mix(in srgb, ${GROUPE_COULEURS[g.nom] || 'var(--muted)'} 14%, transparent)`,
                          color: GROUPE_COULEURS[g.nom] || 'var(--muted)',
                        }}
                      >
                        {g.nom}
                      </span>
                    </td>
                    <td>{g.count} paires</td>
                    <td>{g.poidsMoyen}g ({(g.poidsMoyen / 1000).toFixed(3)}kg)</td>
                    <td className="success">{g.prix > 0 ? `${g.prix}$` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="esc-table">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Groupe</th>
                  <th>Poids</th>
                  <th>Prix de vente</th>
                </tr>
              </thead>
              <tbody>
                {pairesGroupees.map(p => {
                  const prix = modePrixVente === 'kg'
                    ? Math.round((p.total_g / 1000) * prixVenteKgNum)
                    : prixVenteUniteNum;
                  const sku = `RIB-${batchCode}-P${String(p.numero).padStart(3, '0')}`;
                  return (
                    <tr key={p.id}>
                      <td className="font-medium gold">{sku}</td>
                      <td>
                        <span
                          className="badge"
                          style={{
                            background: `color-mix(in srgb, ${GROUPE_COULEURS[p.groupe] || 'var(--muted)'} 14%, transparent)`,
                            color: GROUPE_COULEURS[p.groupe] || 'var(--muted)',
                          }}
                        >
                          {p.groupe}
                        </span>
                      </td>
                      <td>{p.total_g}g ({(p.total_g / 1000).toFixed(3)}kg)</td>
                      <td className="success">{prix > 0 ? `${prix}$` : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Saisie rapide poids cru — bloquant pour la déduction Côtes de flanc */}
      {batch.statut !== 'finalise' && (
        <div className="card" style={{
          marginTop: 16,
          padding: 14,
          background: !poidsCruSauve ? 'color-mix(in srgb, var(--danger) 10%, transparent)' : 'rgba(255,255,255,0.03)',
          border: !poidsCruSauve ? '1px solid color-mix(in srgb, var(--danger) 35%, transparent)' : '1px solid var(--border)',
        }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="form-field" style={{ flex: '0 0 220px' }}>
              <label style={{ fontWeight: 700 }}>
                Poids cru acheté (kg) {!poidsCruSauve && <span className="danger">*</span>}
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={poidsCruInput}
                onChange={e => setPoidsCruInput(e.target.value)}
                onBlur={sauverPoidsCru}
                placeholder="ex: 16.0"
                disabled={poidsCruSaving}
              />
            </div>
            <div style={{ flex: 1, minWidth: 220 }}>
              {!poidsCruSauve ? (
                <p className="danger" style={{ margin: 0, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Icon name="attention" size={14} /> Sans poids cru, les Côtes de flanc ne sont pas déduites de l'inventaire et le coût matières est sous-estimé.
                </p>
              ) : (
                <p className="success" style={{ margin: 0, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Icon name="check" size={14} weight="bold" /> Poids cru enregistré : {poidsCruSauve} kg
                  {apercuDeduction?.stats?.nb_paires > 0 && (
                    <span className="muted">
                      {' '}— {(poidsCruSauve / apercuDeduction.stats.nb_paires).toFixed(2)} kg/paire moyen
                    </span>
                  )}
                </p>
              )}
            </div>
            {poidsCruSaving && <span className="muted">Enregistrement…</span>}
          </div>
        </div>
      )}

      {/* Stats live yield / coût / marge — avant finalisation
          Revenu et marge calculés côté frontend → réagissent en temps réel aux changements
          de prix de vente (sans attendre que le backend recalcule les paires). */}
      {apercuDeduction?.stats && (() => {
        const stats = apercuDeduction.stats;
        // Revenu live basé sur les prix de vente courants (mode kg ou unité)
        const revenuLive = (() => {
          if (modePrixVente === 'kg' && prixVenteKgNum > 0) {
            return Math.round(paires.reduce((s, p) => s + (p.total_g / 1000) * prixVenteKgNum, 0));
          }
          if (modePrixVente === 'unite' && prixVenteUniteNum > 0) {
            return Math.round(paires.length * prixVenteUniteNum * 100) / 100;
          }
          return stats.revenu_estime;
        })();
        const margeLive = (revenuLive != null && revenuLive > 0 && apercuDeduction.cout_total_batch != null)
          ? Math.round((revenuLive - apercuDeduction.cout_total_batch) / revenuLive * 10000) / 100
          : null;

        return (
          <div className="rentabilite-grid-card">
            <h4 className="rentabilite-titre">Rentabilité — calcul live</h4>
            <div className="rentabilite-grid">
              <div className="rentabilite-item">
                <div className="rentabilite-label">Coût matières / paire</div>
                <div className="rentabilite-valeur gold">{stats.cout_par_paire != null ? `${stats.cout_par_paire.toFixed(2)} $` : '—'}</div>
              </div>
              <div className="rentabilite-item">
                <div className="rentabilite-label">Coût matières / kg</div>
                <div className="rentabilite-valeur gold">{stats.cout_par_kg != null ? `${stats.cout_par_kg.toFixed(2)} $/kg` : '—'}</div>
              </div>
              <div className="rentabilite-item">
                <div className="rentabilite-label">Yield (cuit / cru)</div>
                <div className={`rentabilite-valeur ${stats.yield_global_pct == null ? 'muted' : stats.yield_global_pct >= 70 ? 'success' : stats.yield_global_pct >= 55 ? 'warning' : 'danger'}`}>
                  {stats.yield_global_pct != null ? `${stats.yield_global_pct.toFixed(1)} %` : 'N/A'}
                </div>
                {stats.poids_cru_kg && (
                  <div className="muted" style={{ fontSize: 11 }}>{stats.poids_total_kg} kg / {stats.poids_cru_kg} kg</div>
                )}
              </div>
              <div className="rentabilite-item">
                <div className="rentabilite-label">Revenu estimé</div>
                <div className="rentabilite-valeur gold">{revenuLive != null ? `${Number(revenuLive).toFixed(2)} $` : '—'}</div>
              </div>
              <div className="rentabilite-item">
                <div className="rentabilite-label">Marge brute</div>
                <div className={`rentabilite-valeur ${margeLive == null ? 'muted' : margeLive >= 50 ? 'success' : margeLive >= 30 ? 'warning' : 'danger'}`}>
                  {margeLive != null ? `${margeLive.toFixed(1)} %` : '—'}
                </div>
              </div>
              <div className="rentabilite-item">
                <div className="rentabilite-label">Paires</div>
                <div className="rentabilite-valeur gold">{stats.nb_paires || 0}</div>
              </div>
            </div>
            {stats.yield_global_pct == null && (
              <p className="muted" style={{ fontSize: 12, marginTop: 10, marginBottom: 0 }}>
                Saisis le <strong>poids cru acheté (kg)</strong> ci-dessus pour activer le yield, la déduction Côtes de flanc et le coût réel.
              </p>
            )}
          </div>
        );
      })()}

      {apercuDeduction && apercuDeduction.deductions && apercuDeduction.deductions.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <h4 style={{ marginBottom: 10 }}>Aperçu — Déduction inventaire</h4>
          <p className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
            Ces quantités seront déduites de l'inventaire à la finalisation.
          </p>
          <table className="esc-table">
            <thead>
              <tr><th>Ingrédient</th><th>Déduit</th><th>Stock avant</th><th>Stock après</th></tr>
            </thead>
            <tbody>
              {apercuDeduction.deductions.map(d => (
                <tr key={d.matiere_id}>
                  <td>{d.nom}</td>
                  <td className="gold">−{d.quantite_deduite} {d.unite}</td>
                  <td className="muted">{d.stock_avant} {d.unite}</td>
                  <td className={d.statut === 'negatif' ? 'danger' : d.statut === 'bas' ? 'warning' : 'success'}>
                    {d.stock_apres} {d.unite}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {apercuDeduction.deductions.some(d => d.statut !== 'ok') && (
            <p className="warning" style={{ marginTop: 8, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="attention" size={14} /> {apercuDeduction.deductions.filter(d => d.statut !== 'ok').length} ingrédient(s) passeront sous le seuil après déduction.
            </p>
          )}
        </div>
      )}

      {erreur && <p className="erreur-msg">{erreur}</p>}
      {pdfErreur && <p className="erreur-msg">{pdfErreur}</p>}

      <div className="etape-actions">
        <button
          className="btn btn-ghost"
          onClick={exporterWooCommerce}
          disabled={pairesGroupees.length === 0}
          title="Générer le fichier CSV d'import WooCommerce"
        >
          <Icon name="telecharger" size={14} /> WooCommerce CSV
        </button>
        <button
          className="btn btn-ghost"
          onClick={telechargerPDF}
          disabled={pdfLoading || paires.length === 0}
        >
          <Icon name="telecharger" size={14} /> {pdfLoading ? 'Génération...' : 'Télécharger PDF'}
        </button>
        <button
          className="btn btn-primary btn-lg"
          onClick={finaliser}
          disabled={loading || pairesGroupees.length === 0}
        >
          <Icon name="succes" size={15} /> {loading ? 'Finalisation...' : 'Finaliser et créer les produits'}
        </button>
      </div>
    </div>
  );
}

// ─── COMPOSANT PRINCIPAL ─────────────────────

export default function ProductionRibs() {
  const [etape, setEtape] = useState(1);
  const [batch, setBatch] = useState(null);
  const [nbTracabiliteNonLies, setNbTracabiliteNonLies] = useState(0);
  const isFinalise = batch?.statut === 'finalise';

  const batchCree = (b) => {
    setBatch(b);
    setEtape(b.statut === 'finalise' ? 5 : 2);
  };

  const reset = () => {
    setBatch(null);
    setEtape(1);
  };

  // Stepper cliquable : sauts arrière libres, sauts avant si étape précédente atteinte.
  // L'étape 1 avec un batch ouvert affiche EtapeBatchEditer (pas de reset).
  const handleStepClick = (cible) => {
    if (!batch && cible > 1) return;
    if (cible < etape) { setEtape(cible); return; }
    if (isFinalise) { setEtape(cible); return; }
    if (cible <= etape + 1) setEtape(cible);
  };

  return (
    <div className="production-ribs">
      <div className="page-header">
        <div>
          <h1 className="page-title">Production — Côtes Levées</h1>
          <p className="page-subtitle">Paires St-Louis · Groupement · WooCommerce</p>
        </div>
        {batch && (
          <button className="btn btn-ghost btn--sm" onClick={reset}>
            <Icon name="ajouter" size={14} /> Nouveau batch
          </button>
        )}
      </div>

      <Stepper
        etape={etape}
        nbTracabiliteNonLies={nbTracabiliteNonLies}
        onStepClick={handleStepClick}
        batch={batch}
        isFinalise={isFinalise}
      />

      {etape === 1 && (batch
        ? <EtapeBatchEditer batch={batch} setBatch={setBatch} />
        : <EtapeBatch onBatchCree={batchCree} />
      )}
      {etape === 2 && batch && (
        <EtapeTracabilite
          batchId={batch?.id}
          batchNom={batch?.nom}
          batchStatut={batch?.statut}
          onNext={() => setEtape(3)}
          onBack={() => setEtape(1)}
          onNbNonLiesChange={setNbTracabiliteNonLies}
        />
      )}
      {etape === 3 && batch && (
        <EtapeSaisie batch={batch} setBatch={setBatch} onSuivant={() => setEtape(4)} />
      )}
      {etape === 4 && batch && (
        <EtapeGroupes batch={batch} setBatch={setBatch} onSuivant={() => setEtape(5)} />
      )}
      {etape === 5 && batch && (
        <EtapeFinaliser batch={batch} onFinalise={reset} />
      )}
    </div>
  );
}
